import { Avatar, Box, Drawer, Grid, SwipeableDrawer, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { deepPurple } from '@mui/material/colors';
import { Logout } from '@mui/icons-material';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Typography from '@mui/material/Typography';

import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import 'moment/dist/locale/he';
import { NavigationItemButton } from './NavigationItemButton';
import { NavigationHeaderButton } from './NavigationHeaderButton';
import { useLanguage } from '../../common/GeneralUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, Currency } from '../../api';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';

export const mainSidebarWidth = 260;
export type NavigationItem = {
  key: string;
  name: string;
  icon?: string;
  navigate: string;
  external?: boolean;
  target?: string;
};

export type NavigationHeader = NavigationItem & {
  navigationSubItems?: (NavigationItem | undefined)[];
};
const MainSidebar = (props: any) => {
  const navigate = useNavigate();
  const { user } = useAuthInfo();
  const logoutFn = useLogoutFunction();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const whatsappIntegrationEnabled = useFeatureFlagEnabled('whatsapp-integration');
  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutDialog = (confirmed: boolean) => {
    setShowLogoutDialog(false);
    if (confirmed) {
      void logoutFn(true);
    }
  };
  const titleCase = (title?: string) => {
    if (!title) {
      return '';
    }

    return title[0].toUpperCase() + title.slice(1).toLowerCase();
  };
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const { isSidebarOpen, setIsSidebarOpen } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const activityLabel = account?.activity_label_plural?.[language];

  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const stripeEnabled = account.currency === Currency.USD;
  const navigations: (NavigationHeader | undefined)[] = [
    {
      key: 'calendar',
      name: t('main_sidebar.calendar'),
      icon: 'calendar_today',
      navigate: '/dashboard/calendar',
    },
    !readOnly
      ? {
          key: 'orders',
          name: t('main_sidebar.orders'),
          icon: 'people',
          navigate: '/dashboard/orders',
        }
      : undefined,
    !readOnly
      ? {
          key: 'vouchers',
          name: t('main_sidebar.vouchers'),
          icon: 'local_activity',
          navigate: '/dashboard/vouchers',
        }
      : undefined,
    !readOnly
      ? {
          key: 'paymentLinks',
          name: t('main_sidebar.paymentLinks'),
          icon: 'link',
          navigate: '/dashboard/payment-links',
        }
      : undefined,
    !readOnly
      ? {
          key: 'waitingList',
          name: t('main_sidebar.waitingList'),
          icon: 'hourglass_bottom',
          navigate: '/dashboard/waiting-list',
        }
      : undefined,

    {
      key: 'order-page',
      name: t('main_sidebar.order_pages', { activityLabel }),
      icon: 'shopping_cart',
      navigate: '/dashboard/order-pages',
    },
    {
      key: 'discounts',
      name: t('main_sidebar.discounts'),
      icon: 'percent',
      navigate: `/dashboard/discounts`,
    },
    {
      key: 'employees',
      name: t('main_sidebar.employees'),
      icon: 'group',
      navigate: `${import.meta.env.VITE_REACT_APP_AUTH_URL}/org`,
      external: true,
      target: '_blank',
    },
    {
      key: 'user-settings',
      name: t('main_sidebar.usersettings'),
      icon: 'settings',
      navigate: '/dashboard/user-settings',
    },
    isAtLeastSupervisor
      ? {
          key: 'settings',
          name: t('main_sidebar.settings'),
          icon: 'settings',
          navigate: `/dashboard/account-settings`,
          navigationSubItems: [
            { key: 'account-settings', navigate: '/dashboard/account-settings', name: t('main_sidebar.settings') },
            {
              key: 'email-templates',
              navigate: '/dashboard/email-templates',
              name: t('main_sidebar.email_templates'),
            },
            account.currency === Currency.USD
              ? {
                  key: 'sms-templates',
                  navigate: '/dashboard/sms-templates',
                  name: t('main_sidebar.sms_templates'),
                }
              : undefined,
            whatsappIntegrationEnabled
              ? {
                  key: 'whatsapp-settings',
                  navigate: '/dashboard/whatsapp-settings',
                  name: t('main_sidebar.whatsapp_settings'),
                }
              : undefined,
            stripeEnabled
              ? {
                  key: 'stripe',
                  name: t('main_sidebar.stripe'),
                  icon: 'attach_money',
                  navigate: `/dashboard/stripe`,
                }
              : undefined,
            account.svix_application_id
              ? {
                  key: 'svix-portal',
                  name: t('main_sidebar.svix_portal'),
                  icon: 'webhook',
                  navigate: `/dashboard/webhooks`,
                }
              : undefined,
          ],
        }
      : undefined,
    !readOnly
      ? {
          key: 'reports',
          name: t('main_sidebar.reports'),
          icon: 'table_rows',
          navigate: `/dashboard/reports`,
        }
      : undefined,
    !readOnly
      ? {
          key: 'analytics',
          name: t('main_sidebar.analytics'),
          icon: 'equalizer',
          navigate: `/dashboard/analytics`,
        }
      : undefined,
    !readOnly
      ? {
          key: 'audit-log',
          name: t('main_sidebar.audit_log'),
          icon: 'manage_search',
          navigate: `/dashboard/audit-log`,
        }
      : undefined,
  ];

  const changeLang = (lang: string) => {
    void i18n.changeLanguage(lang);
    document.body.dir = i18n.dir();
    theme.direction = i18n.dir();
    moment.locale(lang);
    localStorage.setItem('calendar_language', lang);
  };
  // @ts-ignore
  // eslint-disable-next-line no-unsafe-optional-chaining
  const userName = user ? user?.firstName?.[0].toUpperCase() + user.lastName?.[0].toUpperCase() : '';
  const drawer = (
    <Grid
      container
      direction="column"
      flexWrap="nowrap"
      sx={{
        height: '100%',
        width: 260,
        backgroundImage: 'linear-gradient(rgba(34, 42, 69, 0.96), rgba(34, 42, 69, 0.96))',
        color: 'white',
      }}>
      <Grid item container justifyContent="space-between" alignItems="center" flexWrap={'nowrap'} px={2} py={2}>
        <Grid item>
          <Box
            width="160px"
            component="img"
            src="/images/logos/monkeybook-dark.png"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/dashboard/calendar')}
          />
        </Grid>
        <Grid container sx={{ cursor: 'pointer', fontSize: '14px' }} justifyContent="flex-end" gap={1}>
          <Grid onClick={() => changeLang('he')} sx={{ fontWeight: language === 'he' ? 'bold' : 'normal' }}>
            he
          </Grid>
          {' | '}
          <Grid onClick={() => changeLang('en-US')} sx={{ fontWeight: language === 'en' ? 'bold' : 'normal' }}>
            en
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
          },
        }}>
        <Grid item container direction="column" px={2} pt={2}>
          {navigations.map((item) => {
            if (!item) return null;
            if (!isEmpty(item.navigationSubItems)) {
              return <NavigationHeaderButton navigationHeader={item} setIsSidebarOpen={setIsSidebarOpen} />;
            }
            return <NavigationItemButton setIsSidebarOpen={setIsSidebarOpen} navigationItem={item} />;
          })}
        </Grid>
      </Grid>

      <Grid
        item
        p={2}
        sx={{
          marginTop: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
        {user && (
          <Grid container direction="column" spacing={1}>
            <Grid item container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography>{`${titleCase(user?.firstName)} ${titleCase(user?.lastName)}`}</Typography>
              </Grid>
              <Grid item>
                <Avatar
                  sx={{
                    bgcolor: deepPurple[500],
                    width: 32,
                    height: 32,
                  }}>
                  <Typography>{userName}</Typography>
                </Avatar>
              </Grid>
            </Grid>

            <Grid
              item
              container
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              onClick={handleLogoutClick}
              sx={{
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <Grid item>
                <Logout fontSize="small" />
              </Grid>
              <Grid item>
                <Typography>{t('top_bar.logout')}</Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );

  return (
    <>
      <SwipeableDrawer
        onOpen={() => setIsSidebarOpen(true)}
        anchor="left"
        variant="temporary"
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: mainSidebarWidth,
          },
        }}>
        {drawer}
      </SwipeableDrawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'none', md: 'block' },
          '& .MuiPaper-root': {
            overflowX: 'hidden',
            borderLeft: 'none',
          },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: mainSidebarWidth,
            zIndex: '100',
          },
        }}
        open>
        {drawer}
      </Drawer>
      <ConfirmationDialog
        isOpen={showLogoutDialog}
        onClose={handleLogoutDialog}
        title={t('common.are_you_sure')}
        content={t('common.logout_confirmation')}
        ctaText={t('top_bar.logout')}
        confirmButtonColor="primary"
      />
    </>
  );
};

export default MainSidebar;
